import { useCallback, useEffect, useState } from 'react';
import io from 'socket.io-client';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { invalidateSession } from '../../services/sessionSecurity';
import { verifyCurrentSession } from '../../services/sessionVerification';

let activeSocket: ReturnType<typeof io> | null = null;
export function disconnectActiveSocket() {
  activeSocket?.removeAllListeners();
  activeSocket?.disconnect();
  activeSocket = null;
}

export const useSocket = () => {
  const { company, token } = useSelector(
    (state: { user: { token?: string | null; company?: { _id?: string } } }) => state.user
  );
  const dispatch = useDispatch();
  const companyId = company?._id;
  const [socket, setSocket] = useState<ReturnType<typeof io> | null>(null);
  const [online, setOnline] = useState(false);

  useEffect(() => {
    if (!companyId || !token) return;
    const current = io(import.meta.env.REACT_APP_BACKEND_BASE_URL, {
      transports: ['websocket'],
      autoConnect: false,
      query: { 'x-token': token, 'x-store': 'NO_STORE_WEB_SOCKET_CHANGED', 'x-company': companyId }
    });
    let stopped = false;
    let verificationUnavailable = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    activeSocket = current;
    setSocket(current);
    const stillCurrent = () => !stopped && activeSocket === current && Cookies.get('token') === token;
    const clearRetry = () => {
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      retryTimer = undefined;
    };
    const retryConnection = () => {
      if (!stillCurrent() || retryTimer !== undefined) return;
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        if (stillCurrent()) current.connect();
      }, 5_000);
    };
    const onUnavailable = () => {
      if (!stillCurrent()) return;
      verificationUnavailable = true;
      setOnline(false);
      // Server disconnects and rejected handshakes disable Socket.IO's automatic reconnect.
      retryConnection();
    };
    current.on('connect', () => {
      if (!stillCurrent()) return;
      clearRetry();
      verificationUnavailable = false;
      setOnline(true);
      void verifyCurrentSession(dispatch);
    });
    current.on('auth:revoked', () => {
      if (stillCurrent()) invalidateSession(token, 'revoked');
    });
    current.on('auth:unavailable', onUnavailable);
    current.on('disconnect', async (reason: string) => {
      if (stopped) return;
      setOnline(false);
      if (reason === 'io client disconnect') clearRetry();
      if (reason === 'io server disconnect' && stillCurrent()) {
        if (verificationUnavailable) return retryConnection();
        const valid = await verifyCurrentSession(dispatch);
        if (valid && stillCurrent()) current.connect();
      }
    });
    current.on('connect_error', (error: Error & { data?: { code?: string } }) => {
      if (!stillCurrent()) return;
      setOnline(false);
      if (error?.data?.code === 'SESSION_REVOKED' || error?.message === 'SESSION_REVOKED')
        invalidateSession(token, 'revoked');
      else if (error?.data?.code === 'SESSION_SERVICE_UNAVAILABLE') onUnavailable();
      else void verifyCurrentSession(dispatch);
    });
    current.connect();
    return () => {
      stopped = true;
      clearRetry();
      current.removeAllListeners();
      current.disconnect();
      if (activeSocket === current) activeSocket = null;
      setSocket(null);
      setOnline(false);
    };
  }, [companyId, token, dispatch]);

  const disconnectSocket = useCallback(() => {
    disconnectActiveSocket();
    setOnline(false);
  }, []);
  return { socket, online, disconnectSocket };
};
